import React from 'react';
import { Sparkles, Heart, Home, Umbrella, ArrowRight, ShieldCheck } from 'lucide-react';
import { Input } from '../../../components/ui/Input';

export default function DeductionsNiches({ data, onChange, calculations }) {
  const formatEuro = (val) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(val || 0);

  const handleFieldChange = (field, val) => {
    onChange(prev => ({ ...prev, [field]: val }));
  };

  return (
    <div className="space-y-6">
      {/* 1. Épargne Retraite (PER / Madelin) */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center justify-between pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Umbrella className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Déduction Épargne Retraite (PER Individuel / Collectif)</h3>
              <p className="text-xs text-subtle">Déduction intégrale du revenu imposable à hauteur de votre TMI ({calculations.tmi}%).</p>
            </div>
          </div>
          <span className="px-3 py-1 rounded-full bg-surface-muted text-xs font-bold text-primary">
            Plafond Estimé : {formatEuro(calculations.plafondPerTheorique)}
          </span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-5 space-y-3">
            <label className="block text-xs font-medium text-subtle">
              Versement volontaire déductible (€)
            </label>
            <Input
              type="number"
              min="0"
              max={calculations.plafondPerTheorique * 2}
              step="1000"
              value={data.versementPer || ''}
              onChange={(e) => handleFieldChange("versementPer", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="0"
              className="text-lg font-black text-secondary"
            />
            <input
              type="range"
              min="0"
              max={Math.max(50000, calculations.plafondPerTheorique)}
              step="500"
              value={data.versementPer || 0}
              onChange={(e) => handleFieldChange("versementPer", parseInt(e.target.value) || 0)}
              className="w-full accent-secondary cursor-pointer"
            />
          </div>

          {/* Résultat visuel de l'économie d'impôt */}
          <div className="md:col-span-7 grid grid-cols-2 gap-4 p-4 rounded-xl bg-secondary/5 border border-secondary/20">
            <div>
              <span className="text-[11px] font-bold text-subtle uppercase">Économie d'impôt directe</span>
              <p className="text-2xl font-black text-success-600 mt-1">
                -{formatEuro(calculations.gainFiscalPer)}
              </p>
              <span className="text-[11px] text-subtle">Grâce à votre TMI de {calculations.tmi}%</span>
            </div>

            <div>
              <span className="text-[11px] font-bold text-subtle uppercase">Effort réel d'épargne</span>
              <p className="text-2xl font-black text-primary mt-1">
                {formatEuro(calculations.effortReelPer)}
              </p>
              <span className="text-[11px] text-subtle">Pour {formatEuro(data.versementPer || 0)} capitalisés</span>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Crédits et Réductions d'impôt courantes */}
      <div className="workspace-card p-6 bg-surface border border-workspace rounded-2xl shadow-hp-sm">
        <div className="flex items-center justify-between pb-4 border-b border-workspace mb-5">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-secondary" />
            <div>
              <h3 className="text-base font-bold text-primary">Dons, Emploi à Domicile & Investissements Défiscalisants</h3>
              <p className="text-xs text-subtle">Sous réserve du plafonnement global des niches fiscales (10 000 € / an).</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Emploi à Domicile / Garde (€)</label>
            <Input
              type="number"
              min="0"
              value={data.emploiDomicile || ''}
              onChange={(e) => handleFieldChange("emploiDomicile", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="Dépenses engagées..."
            />
            <span className="text-[11px] text-subtle mt-1 block">Crédit d'impôt de 50%</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Dons Coluche (75%) (€)</label>
            <Input
              type="number"
              min="0"
              value={data.donsColuche || ''}
              onChange={(e) => handleFieldChange("donsColuche", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="Jusqu'à 1 000 €..."
            />
            <span className="text-[11px] text-subtle mt-1 block">Organismes d'aide gratuite</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Dons Intérêt Général (66%) (€)</label>
            <Input
              type="number"
              min="0"
              value={data.donsGeneral || ''}
              onChange={(e) => handleFieldChange("donsGeneral", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="Associations, fondations..."
            />
            <span className="text-[11px] text-subtle mt-1 block">Dans la limite de 20% du revenu</span>
          </div>

          <div>
            <label className="block text-xs font-medium text-subtle mb-1.5">Défiscalisation Directe (€)</label>
            <Input
              type="number"
              min="0"
              value={data.investissementsDefisc || ''}
              onChange={(e) => handleFieldChange("investissementsDefisc", Math.max(0, parseInt(e.target.value) || 0))}
              placeholder="Pinel, Girardin, FIP..."
            />
            <span className="text-[11px] text-subtle mt-1 block">Réduction nette d'impôt</span>
          </div>
        </div>

        <div className="mt-5 pt-3.5 border-t border-workspace flex flex-col sm:flex-row sm:items-center justify-between text-xs gap-2">
          <span className="text-subtle">
            Total des niches fiscales mobilisées : <strong className="text-primary">{formatEuro(calculations.totalNichesFiscales)}</strong> / 10 000 €
          </span>
          <span className="text-success-600 font-bold">
            Total réductions & crédits imputés : -{formatEuro(calculations.totalReductionsCredits)}
          </span>
        </div>
      </div>
    </div>
  );
}

